import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import SingleDotIcon from '@/icons/single-dot'
import SectionTitle from '@/components/shared/molecules/SectionTitle'
import LookOptionsBottomSheet from '@/components/looks/bottom-sheets/LookOptionsBottomSheet'
import { LookHeader } from '@/components/looks/LookHeader'
import LookBanner from '@/components/looks/LookBanner'
import { ReportLookFormBottomSheet } from '@/components/looks/bottom-sheets/ReportLookBottomSheet'
import { AddLookToCollectionBottomSheet } from '@/components/looks/bottom-sheets/AddLookToCollectionBottomSheet'
import { ProductMasonryGrid } from '@/components/products/ProductMasonryGrid'
import { useLook } from '@/hooks/api/useLooks'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useBottomNav } from '@/contexts/BottomNavContext'
import { classNames } from '@/utils/classNames'

const Skeleton = () => {
    return (
        <div className="mt-2 animate-pulse space-y-2 px-2">
            <div className="min-h-120 w-full rounded-lg bg-gray-200" />
            <div className="min-h-20 w-full rounded-lg bg-gray-200" />
            {Array.from({ length: 5 }).map((_, index) => (
                <div
                    key={index}
                    className="grid w-full grid-cols-2 items-center gap-2 rounded-lg"
                >
                    <div className="col-span-1 h-60 rounded-2xl bg-gray-200" />
                    <div className="col-span-1 h-60 rounded-2xl bg-gray-200" />
                </div>
            ))}
        </div>
    )
}

export const Route = createFileRoute('/looks/$id')({
    component: LookPageRoute,
})

function LookPageRoute() {
    // All hooks must be called before any early return
    const { id: lookId } = useParams({ from: '/looks/$id' })
    const navigate = useNavigate()
    const { data: look, isLoading: isLookLoading } = useLook(lookId)
    const { user } = useCurrentUser()
    const { isBottomNavVisible, bottomNavHeight } = useBottomNav()

    // Determine if the current user is the owner of the look
    const isOwner = !!(look && user && look.ownerId === user.uid)

    // State for bottom sheets and like
    const [isLookOptionsOpen, setIsLookOptionsOpen] = useState(false)
    const [isReportFormOpen, setIsReportFormOpen] = useState(false)
    const [isAddToCollectionOpen, setIsAddToCollectionOpen] = useState(false)
    const [isLiked, setIsLiked] = useState(look?.isLikedByUser ?? false)

    // Keep isLiked in sync with look data
    useEffect(() => {
        if (look) setIsLiked(look.isLikedByUser)
    }, [look])

    // Handle actions from LookOptionsBottomSheet
    const handleLookActionSelect = (action: 'edit' | 'report' | 'share') => {
        setIsLookOptionsOpen(false)
        if (isOwner && action === 'edit') {
            navigate({ to: `/looks/${lookId}/edit` })
        } else if (action === 'report') {
            setIsReportFormOpen(true) // Open the report form if report action is selected
        } else if (action === 'share') {
            // Placeholder for share functionality
            console.log('Share action selected')
        }
    }

    // Loading and error states
    if (isLookLoading) {
        return <Skeleton />
    }
    if (!look) {
        return (
            <div className="mx-auto flex min-h-dvh w-full max-w-lg items-center justify-center bg-white">
                <span>Look not found</span>
            </div>
        )
    }

    // Get productIds from look, fallback to empty array
    const productIds: string[] = look.productIds || []

    return (
        <>
            {/* Main container for the look page content */}
            <div
                className={classNames(
                    'relative flex min-h-dvh w-full flex-col overflow-hidden bg-white pt-1',
                    isBottomNavVisible ? `mb-${bottomNavHeight}` : undefined
                )}
            >
                {/* Hero section displaying the main look image/video */}
                <LookHeader
                    image={look.imageUrl ?? ''}
                    // showCloseIcon={false}
                    showProducts={false}
                    onReport={() => setIsLookOptionsOpen(true)}
                    showActionLeft={true}
                    showActionRight={true}
                />
                {/* Information section for the look (title, like/save buttons) */}
                <LookBanner
                    title={look.description}
                    ownerId={look.ownerId}
                    isLiked={isLiked}
                    onLikeClick={() => setIsLiked((prev) => !prev)}
                    onSaveClick={() => setIsAddToCollectionOpen(true)}
                />
                {/* Section displaying the products associated with the look */}
                <section className="mx-2 mt-4 border-t border-[#f0f0f0] pt-3">
                    <SectionTitle
                        icon={
                            <SingleDotIcon
                                fill="black"
                                width={16}
                                height={16}
                            />
                        }
                        title={'Products'}
                        showRightTitle={false}
                        containerClassName="mb-1 flex justify-between"
                    />

                    {/* Empty state */}
                    {productIds.length === 0 && (
                        <div className="py-8 text-center text-gray-400">
                            <p>No products in this look yet.</p>
                        </div>
                    )}
                    {/* Masonry layout for product cards */}
                    <ProductMasonryGrid
                        productIds={productIds}
                        editMode={false}
                    />
                </section>
            </div>

            {/* Section containing all the bottom sheets used on this page. */}
            <section className="fixed">
                <LookOptionsBottomSheet
                    lookId={lookId}
                    open={isLookOptionsOpen}
                    onClose={() => setIsLookOptionsOpen(false)}
                    onActionSelect={handleLookActionSelect}
                    isOwner={isOwner}
                />

                <ReportLookFormBottomSheet
                    open={isReportFormOpen}
                    onClose={() => setIsReportFormOpen(false)}
                    lookId={lookId}
                />

                <AddLookToCollectionBottomSheet
                    open={isAddToCollectionOpen}
                    onClose={() => setIsAddToCollectionOpen(false)}
                    lookId={lookId}
                />
            </section>
        </>
    )
}
